import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import type { Bet } from 'src/types/domain/Bet'
import { formatMoney, moneyLenClass } from './formatMoney'
import { clampToPayoutCeiling } from './crashPayoutCeiling'
import { CrashZoneControls } from './CrashZoneControls'
import {
  chipEdgeStripes,
  chipFacePalette,
  chipStackDiscs,
  chipStackLayout,
  chipStackSettleKey,
  chipStyleForAmount,
  chipStyleForIndex,
  type ChipEdgeStripe,
  type ChipFacePalette
} from './chipStack'
import {
  barDisplayMultiplier,
  filterLockedAutoPatch,
  isCashoutArmed,
  isNextRoundBuilder,
  resolveCashoutControlPresence,
  resolveLockedCashoutTerms,
  resolveZoneCardState,
  zoneCardContentGroup,
  type CrashPanelMode,
  type SubmittedCashoutTerms,
  type Zone
} from './crashChipLogic'
import { getDisplayMultiplier } from './useFrameBeacon'
import type { OptimisticCashout, PlaceChipOutcome, ZoneAuto } from './useCrashChipBetting'

// Confirmed-pulse dwell — matches chip-placement's `chipConfirmedPulse`
// (450ms) plus a small clear buffer.
const CONFIRM_PULSE_MS = 550

// Dwell for the transient "Auto bet on/off" confirmation above the zone
// card (the reference notice's ~2.5s, shortened — it's a tooltip, not a
// warning).
const AUTO_TIP_MS = 1800

// A formatted amount — tabular numerals, and the font steps DOWN via the
// `--len-N` classes when the string is long. Never ellipsized, never clipped.
function Money({ value }: Readonly<{ value: string }>) {
  return <span className={moneyLenClass(value)}>{value}</span>
}

// #rrggbb → rgba(...) for the staked-card rim glow (chip border colours are
// plain 6-digit hexes from CHIP_STYLES).
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex)
  if (!m) return `rgba(255, 255, 255, ${alpha})`
  const n = parseInt(m[1]!, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

// ── The poker chip drawing (v12.4, ask 3) ────────────────────────────────
// One real chip, seen at a shallow table angle: an elliptical face over a
// visible rim. Geometry constants shared by every chip in a pile — the
// CHIP_LIFT_PX seat spacing in chipStack.ts is tuned to CHIP_RIM_H so
// stacked chips sit flush like a real stack.
const CHIP_W = 38
const CHIP_H = 17
const CHIP_CX = 19
const CHIP_RX = 17.4
const CHIP_RY = 5.6
const CHIP_FACE_CY = 6.6
const CHIP_RIM_H = 3.4
// The front rim band: the face ellipse's front arc swept down one rim
// height (also the clip for the edge stripes).
const CHIP_RIM_PATH = [
  `M ${CHIP_CX - CHIP_RX} ${CHIP_FACE_CY}`,
  `A ${CHIP_RX} ${CHIP_RY} 0 0 0 ${CHIP_CX + CHIP_RX} ${CHIP_FACE_CY}`,
  `L ${CHIP_CX + CHIP_RX} ${CHIP_FACE_CY + CHIP_RIM_H}`,
  `A ${CHIP_RX} ${CHIP_RY} 0 0 1 ${CHIP_CX - CHIP_RX} ${CHIP_FACE_CY + CHIP_RIM_H}`,
  'Z'
].join(' ')
// Soft light catch across the upper face — part of the chip's material.
const CHIP_SHEEN_PATH = [
  `M ${CHIP_CX - CHIP_RX + 2.6} ${CHIP_FACE_CY - 0.4}`,
  `A ${CHIP_RX - 2.6} ${CHIP_RY - 1.2} 0 0 1 ${CHIP_CX + CHIP_RX - 2.6} ${CHIP_FACE_CY - 0.4}`
].join(' ')

// One SVG poker chip. Purely presentational: palette/stripes arrive
// resolved (pure + tested in chipStack.ts). `uid` namespaces the
// gradient/clip defs — several piles render at once (two zones, harness).
// v13: the chip prints NO value — a banded stack reads by colour and the
// €-amount below the pile states the total; `topLight` marks the pile's
// top disc (it alone catches the table light); `settling` plays the
// one-shot settle drop (the caller grants it to at most ONE new disc —
// chipStackSettleKey — so a restructure never fires an animation storm).
function PokerChip({
  uid,
  palette,
  stripes,
  topLight = false,
  settling = false
}: Readonly<{
  uid: string
  palette: ChipFacePalette
  stripes: ChipEdgeStripe[]
  topLight?: boolean
  settling?: boolean
}>) {
  return (
    <svg
      className={`crash-zone__stack-chip${settling ? ' crash-zone__stack-chip--settle' : ''}`}
      viewBox={`0 0 ${CHIP_W} ${CHIP_H}`}
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
      aria-hidden
    >
      <defs>
        <linearGradient id={`${uid}-face`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.faceTop} />
          <stop offset="1" stopColor={palette.faceBottom} />
        </linearGradient>
        <linearGradient id={`${uid}-rim`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.side} />
          <stop offset="1" stopColor={palette.sideDark} />
        </linearGradient>
        <clipPath id={`${uid}-clip`}>
          <path d={CHIP_RIM_PATH} />
        </clipPath>
        <radialGradient id={`${uid}-glow`} cx="0.5" cy="0.42" r="0.62">
          <stop offset="0" stopColor="rgba(255, 255, 255, 0.2)" />
          <stop offset="1" stopColor="rgba(255, 255, 255, 0)" />
        </radialGradient>
      </defs>
      {/* Underside seam — grounds this chip against the one below. */}
      <ellipse
        cx={CHIP_CX}
        cy={CHIP_FACE_CY + CHIP_RIM_H}
        rx={CHIP_RX}
        ry={CHIP_RY}
        fill={palette.sideDark}
      />
      {/* The rim front, lit top to shadow floor. */}
      <path d={CHIP_RIM_PATH} fill={`url(#${uid}-rim)`} />
      {/* Edge stripes — index-phased so every chip in the pile reads as its
          own rotation of the real thing; widths foreshorten at the edges. */}
      <g clipPath={`url(#${uid}-clip)`}>
        {stripes.map((s) => (
          <rect
            key={s.x}
            // 3dp clamp: full double precision here hydration-mismatches
            // (server/client differ in the last float digit) — v12.4 ask 8
            // capture cleanup.
            x={(CHIP_CX + s.x * CHIP_RX - 2.6 * s.w).toFixed(3)}
            y={CHIP_FACE_CY - 0.5}
            width={(5.2 * s.w).toFixed(3)}
            height={CHIP_RIM_H + CHIP_RY + 1.5}
            fill="#f2efe6"
            opacity="0.92"
          />
        ))}
      </g>
      {/* The face. */}
      <ellipse
        cx={CHIP_CX}
        cy={CHIP_FACE_CY}
        rx={CHIP_RX}
        ry={CHIP_RY}
        fill={`url(#${uid}-face)`}
        stroke="rgba(0, 0, 0, 0.35)"
        strokeWidth="0.5"
      />
      {/* Printed inner ring — the denomination family's accent ink (keeps
          the near-black 500 family legible as more than a silhouette). */}
      <ellipse
        cx={CHIP_CX}
        cy={CHIP_FACE_CY}
        rx={CHIP_RX - 3.1}
        ry={CHIP_RY - 1.35}
        fill="none"
        stroke={palette.ring}
        strokeWidth="1.1"
        strokeDasharray="2.8 2.1"
        opacity="0.8"
      />
      {/* The TOP chip catches the table light — a soft pool on its face
          (buried chips stay in each other's shade like a real stack). */}
      {topLight && (
        <ellipse
          cx={CHIP_CX}
          cy={CHIP_FACE_CY - 0.8}
          rx={CHIP_RX - 4.5}
          ry={CHIP_RY - 2}
          fill={`url(#${uid}-glow)`}
        />
      )}
      <path
        d={CHIP_SHEEN_PATH}
        fill="none"
        stroke="rgba(255, 255, 255, 0.36)"
        strokeWidth="0.9"
        strokeLinecap="round"
      />
    </svg>
  )
}

// The CROUPIER BANDED chip stack (v13; v12.4 poker-chip drawing): the
// staked amount is greedily decomposed over the strip's ladder
// (chipStackDiscs — pure, tested) and drawn as ONE banded pile — largest
// denomination at the base, bands upward in ladder order, each disc in ITS
// OWN family palette. €162 → green 100, orange 50, blue 10, two grey 1s on
// top. Each disc sits at a deterministic index-derived offset/rotation
// (chipStackLayout — no Math.random at render) and its edge stripes carry
// an index-derived rotational phase (chipEdgeStripes).
//
// Discs carry band-relative keys (`denom:i`), so an EXTENDING addition
// keeps every existing disc's node and mounts only the new one — which
// alone plays the settle animation (chipStackSettleKey, pure + tested). A
// greedy re-carve / compression restructure re-renders the pile WITHOUT
// the settle animation (no storm). The top disc prints nothing: a mixed
// stack reads by colour, and the €-amount right below states the total.
// aria-hidden child of the tap surface: it never intercepts pointers and
// the card element itself is untouched (animation rule).
function ZoneChipStack({ amount, chips }: Readonly<{ amount: number; chips: number[] }>) {
  const uid = useId()
  const discs = chipStackDiscs(amount, chips)
  const seats = chipStackLayout(discs.length)
  const keys = discs.map((d) => d.key)

  // Which disc settles this render: compare against the PREVIOUS pile's
  // keys, but only when the pile actually changed — the resolved decision
  // is held so the parent's frequent re-renders (display-clock churn) never
  // strip the class mid-animation. The ref updates post-commit, so a
  // StrictMode double render resolves identically.
  const sig = keys.join('|')
  const historyRef = useRef<{ sig: string; keys: string[]; settleKey: string | null } | null>(null)
  const prev = historyRef.current
  const settleKey =
    prev === null || prev.sig !== sig
      ? chipStackSettleKey(prev === null ? null : prev.keys, keys)
      : prev.settleKey
  useEffect(() => {
    historyRef.current = { sig, keys, settleKey }
  })

  return (
    <div className="crash-zone__stack" aria-hidden>
      {discs.map((disc, i) => {
        const seat = seats[i]!
        const style = chipStyleForIndex(disc.denomIndex)
        return (
          <span
            key={disc.key}
            className="crash-zone__stack-seat"
            style={{
              bottom: `${seat.lift}px`,
              zIndex: i,
              transform: `translateX(calc(-50% + ${seat.dx}px)) rotate(${seat.rot}deg)`
            }}
          >
            <PokerChip
              uid={`${uid}-${i}`}
              palette={chipFacePalette(style.bg, style.border)}
              stripes={chipEdgeStripes(i)}
              topLight={i === discs.length - 1}
              settling={disc.key === settleKey}
            />
          </span>
        )
      })}
    </div>
  )
}

type Props = Readonly<{
  zone: Zone
  label: string
  stake: number
  // Amount locked in for THIS round the moment the submission fired (0 =
  // nothing submitted). Drives the locked/submitted stake card so the zone
  // never goes empty between submit and bet-accepted (chip-placement's
  // placing → confirmed model).
  submittedStake: number
  // The cash-out terms ACTUALLY SUBMITTED with this zone's bet (v10 —
  // snapshotted by the hook the moment the submission fired; undefined =
  // nothing submitted this round). The locked pill states these: the
  // bet:accepted replacement strips autoCashoutMultiplier off the store
  // bet, so myBet alone cannot be trusted mid-round.
  submittedCashout?: SubmittedCashoutTerms
  myBet?: Bet
  // The bet is still awaiting server acceptance (G10) — gates the busted
  // flip: a never-accepted bet must not render as a loss.
  betUnaccepted?: boolean
  liveMultiplier: number
  hasStartedClimbing: boolean
  // Panel-level presentation mode (chip-placement collapse model):
  //   betting        — betting window, full staking target + auto footer.
  //   live-full      — round live, player has NO bets: same full betting
  //                    layout; placed stakes queue for the next round.
  //   live-collapsed — round live with bets riding: the auto footer stays
  //                    at FULL contrast (auto interactive, cash-out locked
  //                    when this zone rides — see cashoutLocked).
  mode: CrashPanelMode
  // Live-round lock (v7, isZoneCashoutLocked): this zone's bet is riding —
  // its auto-cash-out was submitted with the bet (server-side), so the
  // cash-out toggle + stepper freeze at full contrast with a lock glyph.
  // The AUTO toggle stays interactive: it governs the next round's re-bet.
  cashoutLocked: boolean
  /** Operator liability ceiling in DECIMAL units (resolvePayoutCeiling), null
   * when unconfigured. The crash multiplier is uncapped but the PAYOUT is not,
   * so the riding card's live amount freezes here — the backend will not pay
   * past it and the player must never watch a figure it will refuse. */
  payoutCeiling?: number | null
  /** Betting closes within seconds — the cash-out instrument warms amber
   * ("about to lock", v34). */
  closingSoon?: boolean
  /** Tap on the LOCKED instrument's terms → the panel explains the lock. */
  onLockedCashoutTap?: () => void
  /** v41 — the locked toggle flipped the NEXT round's cash-out draft:
   * true = it just went OFF (amber state), false = re-armed. */
  onNextRoundCashoutToggle?: (off: boolean) => void
  // The round crashed backend-side (the crash EVENT is known). Display uses
  // this only to FREEZE the live card during the visual hold — never to flip
  // to Busted.
  hasCrashed: boolean
  // The crash is visible on THIS viewer's frames (useVisualCrash) — the only
  // signal that may flip the zone to its red Busted state. Cashed/won states
  // stay event-driven (player-initiated).
  visualCrashed: boolean
  // The multiplier the round crashed at (from the crash event), shown on the
  // busted card ("@ 2.53x") so the player sees where it went. Undefined
  // until the round crashes.
  crashedMultiplier?: number
  currency: string
  inputPrecision: number
  auto: ZoneAuto
  // Whether a cash-out POST for this zone is currently in flight (disables the
  // card to prevent a double-tap firing a second request).
  cashingOut: boolean
  // The optimistic cash-out overlay (null/undefined = none): the settling
  // card's frozen figures, snapshotted at the tap. Mounted → the card
  // renders the 'cashing' state until the server answers.
  optCashout?: OptimisticCashout | null
  // Fired while a chip is landing on this zone (drives the receive pulse).
  isReceiving: boolean
  // v12: this zone's win banner is mounted above the card — suppress the
  // transient auto-tip while it is (two floating pills over one zone would
  // collide).
  bannerActive?: boolean
  // The strip's denomination ladder (ascending). Drives the growing chip
  // stack (v12.3): depth, disc colour family and the top disc's printed
  // value all derive from the staked amount against these values.
  chips: number[]
  // Returns what the tap DID (v12.4 ask 7): 'placed' fires the flying chip
  // + chip sound, 'refused' the cancel sound (the hook already fired the
  // reasoned notice), 'inert' nothing (tap shield — a reflex, not an action).
  onPlaceChip: (zone: Zone) => PlaceChipOutcome
  // The zone passes the display-clock multiplier it is rendering at the tap
  // so the optimistic settling card can freeze exactly what the player saw.
  onCashout: (zone: Zone, shownMultiplier?: number) => Promise<void>
  onSetAuto: (patch: Partial<ZoneAuto>) => void
  // Clears this zone's queued next-round stake (the staked target's ✕).
  onClearZone: (zone: Zone) => void
  // Emits the chip's flight coordinates so the container can portal the
  // flying chip. Passing the landing rect keeps the animation logic in the
  // container (single portal) while the tap target lives here.
  onFlyTarget?: (rect: DOMRect) => void
}>

/**
 * One crash bet zone (Left / Right) — ONE card, fixed geometry, morphing
 * content (v6).
 *
 * The card (`.crash-zone__surface--card`) keeps the SAME size, radius and
 * position through the entire round; only the content inside swaps, via a
 * quick 180ms crossfade keyed on the content group. The states
 * (`resolveZoneCardState`, pure + tested):
 *
 *   empty   → "Tap to bet" target (betting window / live next-round queue).
 *   staked  → chip stack + amount (chip-rim glow keyed to denomination).
 *   queued  → same + amber "Next round" eyebrow + ✕ (round is live).
 *   placing → same, locked chassis, breathing dim (POST in flight).
 *   locked  → same + amber "Bet placed" tag — THE anchor state.
 *   live    → SAME card, green treatment, whole card tappable:
 *             "Cash out" + live amount (display clock).
 *   busted  → SAME card: "Busted" + "−X" + "@ Yx" red (single shake).
 *
 * (There is NO won card — v12: a cashed bet (won / cashed_out) FREES the
 * zone back into the builder family and the win renders as the derived
 * banner above the zone, CrashZoneWinBanner.)
 *
 * Below the card the footer renders the v8 controls: the platform's auto
 * ICON toggle + ONE cash-out instrument [cashout-icon | − x.xx +]
 * (CrashZoneControls). During a live round with this zone's bet riding
 * (cashoutLocked) the instrument freezes at full contrast showing the
 * riding terms when the bet carries an auto-cash-out — and when the bet
 * was placed with cash-out OFF the pill gives way to the v10 seated-auto
 * chassis (nothing is on the wire to state, so the auto icon relaxes into a
 * compact quiet capsule — the row reads designed, never a floating icon).
 * The AUTO icon stays interactive in every mode since it governs the NEXT
 * round's re-bet.
 *
 * All amounts go through `formatMoney` — a money string is never truncated;
 * the font steps down instead (`moneyLenClass`).
 */
export function CrashBetZone({
  zone,
  label,
  stake,
  submittedStake,
  submittedCashout,
  myBet,
  betUnaccepted = false,
  liveMultiplier,
  hasStartedClimbing,
  mode,
  cashoutLocked,
  payoutCeiling = null,
  closingSoon = false,
  onLockedCashoutTap,
  onNextRoundCashoutToggle,
  hasCrashed,
  visualCrashed,
  crashedMultiplier,
  currency,
  inputPrecision,
  auto,
  cashingOut,
  optCashout = null,
  isReceiving,
  bannerActive = false,
  chips,
  onPlaceChip,
  onCashout,
  onSetAuto,
  onClearZone,
  onFlyTarget
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { playEffect } = useSoundEffect()
  const surfaceRef = useRef<HTMLDivElement>(null)

  const symbol = getCurrencySymbol(currency)
  const isBettingOpen = mode === 'betting'

  // ── ONE display clock for the DISPLAYED numbers (bug c) ────────────────
  // The hero (CrashMultiplierDisplay) publishes its beacon-preferred display
  // multiplier every rAF; the card's live amount and "@ x.xx" read it here —
  // at this component's existing re-render cadence (the event-clock
  // `liveMultiplier` prop churn), no second rAF/sampler — so the two numbers
  // can never contradict under stream lag. `liveMultiplier` (event clock)
  // remains the fallback when no display loop is publishing.
  //
  // The event clock is the CEILING for cash-out arming, not the whole rule.
  // It ran ~syncDelta (2.3s) ahead of the frames, so arming on it ALONE lit
  // the button while the player still saw the 3‥2‥1 countdown — press it and
  // the server paid ~1.04x on a round that, to them, had not started. The
  // display clock is now ANDed in as the FLOOR (isCashoutArmed).
  //
  // MONEY SEMANTICS (unchanged, load-bearing): the cash-out PAYOUT comes
  // from the backend response, settled on the backend's event clock. Frames
  // trail the backend, so the multiplier paid is ≥ the one displayed here —
  // surprises only ever favor the player.
  const shownMultiplier = barDisplayMultiplier(
    getDisplayMultiplier(),
    liveMultiplier,
    crashedMultiplier
  )

  // Freeze reference for the visual-crash hold: the last multiplier the live
  // card actually SHOWED (display clock). When the backend crash lands seconds
  // before the man walks into frame, the card keeps its live look frozen at
  // this value (capped at M) instead of flipping to Busted early.
  const lastLiveMultiplierRef = useRef(1)
  useEffect(() => {
    if (hasStartedClimbing && shownMultiplier > lastLiveMultiplierRef.current) {
      lastLiveMultiplierRef.current = shownMultiplier
    }
    if (isBettingOpen) lastLiveMultiplierRef.current = 1 // new round
  }, [hasStartedClimbing, shownMultiplier, isBettingOpen])

  // Rewarding payoff cue: fire the dedicated `reward` sound once when this
  // zone's bet lands in a winning state (manual cash-out → cashed_out, or
  // auto-cash-out → won). Guarded on the status transition so it fires
  // exactly once, not per render. This zone is crash-only (rendered solely
  // by CrashChipPanel), so the `reward` cue is inherently crash-scoped.
  const prevStatusRef = useRef(myBet?.status)
  useEffect(() => {
    const status = myBet?.status
    const prev = prevStatusRef.current
    prevStatusRef.current = status
    if (status === prev) return
    if (status === 'cashed_out' || status === 'won') playEffect('reward')
  }, [myBet?.status, playEffect])

  // Placing → confirmed pulse (chip-placement's visual language): while the
  // submit is in flight the locked card is `--placing`; the moment the bet id
  // materialises in the store the card fires a one-shot green confirm ring.
  const [confirmedAt, setConfirmedAt] = useState(0)
  const prevBetIdRef = useRef(myBet?.id)
  useEffect(() => {
    const id = myBet?.id
    const prev = prevBetIdRef.current
    prevBetIdRef.current = id
    if (id !== undefined && prev === undefined && submittedStake > 0) {
      setConfirmedAt(Date.now())
      const timer = setTimeout(() => setConfirmedAt(0), CONFIRM_PULSE_MS)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [myBet?.id, submittedStake])

  // No phase gate here — the target only accepts chips in states that take
  // them (betting window, or the live next-round queue). The hook queues
  // live-phase chips for the next round.
  //
  // v12.4 ask 7 (G1/G2): the hook DECIDES first — a refused tap must not
  // fake success. Only a genuine placement fires the flying chip + chip
  // sound; a refusal plays the cancel sound (the hook already surfaced the
  // reasoned notice); a shielded tap stays fully inert.
  const handlePlace = useCallback(() => {
    const outcome = onPlaceChip(zone)
    if (outcome === 'placed') {
      if (surfaceRef.current && onFlyTarget) {
        onFlyTarget(surfaceRef.current.getBoundingClientRect())
      }
      playEffect('chip')
    } else if (outcome === 'refused') {
      playEffect('cancel')
    }
  }, [onFlyTarget, playEffect, onPlaceChip, zone])

  // Crash-hold freeze value + the number the live card renders — hoisted so
  // the tap handler can snapshot EXACTLY what is on screen (WYSIWYG: the
  // optimistic settling card must promise the figure the player saw, never
  // a clock they couldn't see).
  const inCrashHold = hasCrashed && !visualCrashed && lastLiveMultiplierRef.current > 1
  const displayMultiplier = inCrashHold
    ? Math.min(lastLiveMultiplierRef.current, crashedMultiplier ?? Number.POSITIVE_INFINITY)
    : shownMultiplier

  const handleCashout = useCallback(() => {
    // No-op if a cash-out for this zone is already in flight (FIX 4).
    if (cashingOut) return
    playEffect('click')
    void onCashout(zone, displayMultiplier)
  }, [cashingOut, playEffect, onCashout, zone, displayMultiplier])

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      // v49 — the ✕ removes a stake, so it speaks the removal cue
      // ('cancel', same as the trash + reference panel), not the generic
      // click.
      playEffect('cancel')
      onClearZone(zone)
    },
    [playEffect, onClearZone, zone]
  )

  // ── THE card state (pure, tested in crashChipLogic.test.ts) ───────────
  const cardState = resolveZoneCardState({
    isBettingOpen,
    stake,
    submittedStake,
    betStatus: myBet?.status,
    hasStartedClimbing: isCashoutArmed(
      hasStartedClimbing,
      shownMultiplier,
      lastLiveMultiplierRef.current
    ),
    hasCrashed,
    visualCrashed,
    hasEverClimbed: lastLiveMultiplierRef.current > 1,
    betUnaccepted,
    optimisticCashout: optCashout !== null
  })
  const contentGroup = zoneCardContentGroup(cardState)

  // Belt-and-braces for the live lock: even if a locked control somehow
  // fires, cash-out fields are stripped from the patch (pure, tested) — a
  // locked footer can only ever touch the next-round auto toggle.
  const guardedSetAuto = useCallback(
    (patch: Partial<ZoneAuto>) => {
      const safe = filterLockedAutoPatch(cashoutLocked, patch)
      if (Object.keys(safe).length === 0) return
      onSetAuto(safe)
    },
    [cashoutLocked, onSetAuto]
  )

  // While locked, the cash-out controls state the RIDING BET's actual terms
  // — resolved from the submission-time snapshot first (client-owned truth
  // that survives the bet:accepted replacement stripping
  // autoCashoutMultiplier off the store bet — the v10 bug), then the server
  // bet's field (remount mid-round), never invented. Unlocked → the
  // editable draft. Pure + tested (crashChipLogic.test.ts).
  const lockedTerms = resolveLockedCashoutTerms(
    cashoutLocked,
    submittedCashout,
    myBet?.autoCashoutMultiplier,
    auto
  )
  const shownCashoutEnabled = lockedTerms.enabled
  const shownCashoutMultiplier = lockedTerms.multiplier

  // v8 presence: riding with cash-out ON → locked terms; riding with
  // cash-out OFF → the instrument disappears entirely (nothing is on the
  // wire for it to state); otherwise the full editable control.
  const cashoutPresence = resolveCashoutControlPresence(cashoutLocked, shownCashoutEnabled)

  // Transient "Auto bet on/off" confirmation above the zone card (the
  // reference chip-panel notice pattern) — re-keyed per fire so rapid
  // toggles restart the entrance animation.
  const [autoTip, setAutoTip] = useState<{ text: string; key: number } | null>(null)
  useEffect(() => {
    if (!autoTip) return
    const id = setTimeout(() => setAutoTip(null), AUTO_TIP_MS)
    return () => clearTimeout(id)
  }, [autoTip])

  const handleToggleAuto = useCallback(() => {
    const next = !auto.autoEnabled
    guardedSetAuto({ autoEnabled: next })
    setAutoTip({ text: next ? t('Auto bet on') : t('Auto bet off'), key: Date.now() })
  }, [auto.autoEnabled, guardedSetAuto, t])

  // The Auto/Cashout footer: rendered ALWAYS at full contrast (v7). The
  // AUTO icon stays interactive through the live round (next-round re-bet);
  // the cash-out instrument locks — or vanishes — while this zone rides.
  const footer = (
    <div className="crash-zone__footer">
      {autoTip && !bannerActive && (
        <span key={autoTip.key} className="crash-zone__auto-tip" role="status" aria-live="polite">
          {autoTip.text}
        </span>
      )}
      <CrashZoneControls
        autoEnabled={auto.autoEnabled}
        cashoutEnabled={shownCashoutEnabled}
        cashoutMultiplier={shownCashoutMultiplier}
        closingSoon={closingSoon}
        nextRoundCashoutOff={cashoutLocked && !auto.cashoutEnabled}
        presence={cashoutPresence}
        onToggleAuto={handleToggleAuto}
        onToggleCashout={() => {
          const next = !auto.cashoutEnabled
          guardedSetAuto({ cashoutEnabled: next })
          // v41 — while locked this flipped the NEXT round only: surface it.
          if (cashoutLocked) onNextRoundCashoutToggle?.(!next)
        }}
        onSetCashoutMultiplier={(m) => guardedSetAuto({ cashoutMultiplier: m })}
        onLockedTap={onLockedCashoutTap}
      />
    </div>
  )

  // ─────────────────────────────────────────────────────────────────────
  // A) STAKING TARGET — empty / staked / queued. A cancelled (refunded)
  //    bet frees the zone back to a target immediately.
  // ─────────────────────────────────────────────────────────────────────
  if (cardState === 'empty' || cardState === 'staked' || cardState === 'queued') {
    const staked = stake > 0
    const stakeText = formatMoney(stake, inputPrecision)
    // v12.1 (tweak 2): while betting is CLOSED every builder card targets
    // the NEXT round — the amber NEXT ROUND eyebrow REPLACES the BET n
    // eyebrow in the same slot (same size, same ticks, amber ink): one
    // labelling mechanism, per zone, zero geometry change. Queued stakes
    // and the empty "Tap to bet" builder alike.
    const nextRound = isNextRoundBuilder(isBettingOpen, cardState)
    const eyebrow = nextRound ? (
      <span className="crash-zone__label crash-zone__label--eyebrow crash-zone__label--next-round">
        {t('Next round')}
      </span>
    ) : (
      <span className="crash-zone__label crash-zone__label--eyebrow">{label}</span>
    )
    // Rim glow keyed to the staked chip's denomination colour — the card
    // reads as "wearing" the chip that built it. On the QUEUED card the
    // inline rim yields to the amber --queued surface cue (v12); the chip
    // stack below keeps the real chip colours.
    const stakeChipStyle = staked ? chipStyleForAmount(stake, chips) : undefined
    const surfaceClass = [
      'crash-zone__surface',
      'crash-zone__surface--card',
      staked ? 'crash-zone__surface--staked' : '',
      cardState === 'queued' ? 'crash-zone__surface--queued' : '',
      isReceiving ? 'crash-zone__surface--receiving' : ''
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className="crash-zone">
        <div
          ref={surfaceRef}
          className={surfaceClass}
          style={
            stakeChipStyle && cardState !== 'queued'
              ? {
                  borderColor: hexToRgba(stakeChipStyle.border, 0.45),
                  boxShadow: `0 0 0 1px ${hexToRgba(stakeChipStyle.border, 0.35)}, 0 0 18px -6px ${hexToRgba(stakeChipStyle.border, 0.45)}`
                }
              : undefined
          }
          role="button"
          tabIndex={0}
          // v12.2 (minor 6): the accessible name KEEPS the zone identity —
          // with two builders both wearing the NEXT ROUND eyebrow, a screen
          // reader must still hear which is which ("Bet 2 — Next round:
          // €100"). Visible design unchanged.
          aria-label={
            nextRound
              ? `${label} — ${t('Next round')}: ${staked ? `${symbol}${stakeText}` : t('Tap to bet')}`
              : `${label}: ${staked ? `${symbol}${stakeText}` : t('Tap to bet')}`
          }
          onClick={handlePlace}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handlePlace()
            }
          }}
        >
          {staked ? (
            <div key={contentGroup} className="crash-zone__content">
              {/* BET n eyebrow — or the amber NEXT ROUND eyebrow while the
                  builder targets the next round (v12.1 tweak 2). */}
              {eyebrow}
              {/* v13: the CROUPIER BANDED stack — the stake greedily
                  decomposed over the ladder, largest at the base, each disc
                  in its own family palette (pure/tested, chipStack.ts);
                  only a newly added disc animates. */}
              <ZoneChipStack amount={stake} chips={chips} />
              <span className="crash-zone__amount">
                <span className="crash-zone__amount-symbol">{symbol}</span>
                <Money value={stakeText} />
              </span>
              {/* Reserved tag slot — keeps the amount at the exact height it
                  will sit at once the "Bet placed" tag appears at lock. */}
              <span className="crash-zone__tag-slot" aria-hidden />
            </div>
          ) : (
            <div key={contentGroup} className="crash-zone__content">
              {eyebrow}
              {/* v12.3 (tweak 3): the invitation is a ghost chip + a larger,
                  more confident prompt — same quiet family as the eyebrow
                  system, still far below the riding/result states' volume.
                  v12.4 (ask 5): bigger, and the plus is a DRAWN SVG glyph —
                  two centred strokes — so it sits mathematically dead-centre
                  in the circle at every DPR (a text "+" never quite does). */}
              <span className="crash-zone__prompt-badge" aria-hidden>
                <svg viewBox="0 0 14 14" focusable="false" aria-hidden>
                  <path
                    d="M7 2.4v9.2M2.4 7h9.2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
              <span className="crash-zone__prompt">{t('Tap to bet')}</span>
            </div>
          )}
          {cardState === 'queued' && (
            <button
              type="button"
              className="crash-zone__clear"
              aria-label={t('Clear')}
              title={t('Clear')}
              onClick={handleClear}
            >
              &times;
            </button>
          )}
        </div>
        {footer}
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────
  // B) LIVE / RESULT — the SAME card, morphed content.
  // ─────────────────────────────────────────────────────────────────────

  // NOTE (v12, ask 4): there is NO won card any more — a cashed bet FREES
  // the zone into the builder branch above, and the win renders as the
  // derived banner ABOVE the zone (CrashZoneWinBanner, panel-level). The
  // could-have-won tease moved onto the banner with it.

  // Cashing → the OPTIMISTIC settling card (green live family, frozen at
  // the tap's figures, non-interactive). NOT a win: no chime, no banner —
  // both stay store-derived and fire only on the server confirm. Outranks
  // busted while the POST is in flight so a crash landing inside the
  // round-trip can't yank the card red under the player's finger.
  if (cardState === 'cashing' && optCashout) {
    // Clamped like the live card it replaces. This is still a PROJECTION — the
    // POST is in flight and the backend has not confirmed — so without the clamp
    // the figure JUMPS UP past the ceiling the instant the player taps, showing
    // the exact number the clamp exists to prevent, at the highest-attention
    // moment, then walks back down when the server answers. Single bet, so the
    // per-bet ceiling applies directly.
    const cashingText = formatMoney(
      clampToPayoutCeiling(optCashout.payout, payoutCeiling),
      inputPrecision
    )
    return (
      <div className="crash-zone">
        <button
          type="button"
          className="crash-zone__surface crash-zone__surface--card crash-zone__surface--card-live crash-zone__surface--card-cashing"
          aria-label={`${label}: ${t('Cashing out…')} ${symbol}${cashingText}`}
          disabled
        >
          <div key={contentGroup} className="crash-zone__content">
            <span className="crash-zone__label crash-zone__label--eyebrow">{label}</span>
            <span className="crash-zone__cashout-verb">{t('Cashing out…')}</span>
            <span className="crash-zone__amount">
              <span className="crash-zone__amount-symbol">{symbol}</span>
              <Money value={cashingText} />
            </span>
            <span className="crash-zone__tag-row">
              <span className="crash-zone__stake-note">
                {`@ ${optCashout.multiplier.toFixed(2)}x`}
              </span>
            </span>
          </div>
        </button>
        {footer}
      </div>
    )
  }

  // Busted → red card: "Busted", "−X", "@ Yx" (single shake on mount).
  if (cardState === 'busted' && myBet) {
    const lostText = formatMoney(myBet.betAmount, inputPrecision)
    return (
      <div className="crash-zone">
        <div
          className="crash-zone__surface crash-zone__surface--card crash-zone__surface--card-busted"
          aria-label={`${label}: ${t('Busted')} -${symbol}${lostText}`}
        >
          <div key={contentGroup} className="crash-zone__content">
            <span className="crash-zone__label crash-zone__label--eyebrow">{label}</span>
            {/* v11: the busted caption joins the same result-caption system
                as the won card (sentence case, quiet, amount = hero) — one
                typographic family across both outcomes. */}
            <span className="crash-zone__result-caption crash-zone__result-caption--busted">
              {t('Busted')}
            </span>
            <span className="crash-zone__amount crash-zone__amount--busted crash-zone__amount--result">
              {/* No minus token on bust (owner request) — the red "Busted"
                  caption + red amount already read as the loss. The aria-label
                  above keeps the "-" for screen readers. */}
              <span className="crash-zone__amount-symbol">{symbol}</span>
              <Money value={lostText} />
            </span>
            <span className="crash-zone__tag crash-zone__tag--busted">
              {crashedMultiplier !== undefined ? `@ ${crashedMultiplier.toFixed(2)}x` : ' '}
            </span>
          </div>
        </div>
        {footer}
      </div>
    )
  }

  // Live climb (or crash-hold freeze) → the SAME card, green treatment,
  // whole card tappable. The displayed amount and "@ x.xx" ride the display
  // clock (shownMultiplier) — the same number the hero shows.
  if (cardState === 'live' && myBet) {
    // Crash-hold freeze applies only once the card actually SHOWED a climb
    // (same gate as the resolver) — otherwise ride the display clock.
    // (inCrashHold/displayMultiplier are hoisted above handleCashout so the
    // tap snapshots the exact rendered value.)
    const liveText = formatMoney(
      clampToPayoutCeiling(myBet.betAmount * displayMultiplier, payoutCeiling),
      inputPrecision
    )
    // v11: the riding card always states what was staked — quiet companion
    // to the live value so the player reads stake vs current at a glance.
    // Snapshot-first (submittedStakes), betAmount fallback for remounts.
    const ridingStakeText = formatMoney(
      submittedStake > 0 ? submittedStake : myBet.betAmount,
      inputPrecision
    )
    return (
      <div className="crash-zone">
        <button
          type="button"
          className="crash-zone__surface crash-zone__surface--card crash-zone__surface--card-live"
          onClick={handleCashout}
          // NOT disabled during the crash-hold freeze on purpose. The cashout
          // hook already refuses to act then — cashout() early-returns on
          // `isClimbing && liveMultiplier > 1` (useCrashChipBetting.ts), and
          // isClimbing is false once the crash event has landed (== the same
          // moment inCrashHold is true). So a tap in the freeze is already a
          // silent no-op: no request, no error notice. Greying the button out
          // here adds nothing functional and reads as an early "round over"
          // tell ~1.8s before the frames show the crash — so we keep it looking
          // live and let the frozen value be the only, subtler signal.
          disabled={cashingOut}
        >
          <div key={contentGroup} className="crash-zone__content">
            <span className="crash-zone__label crash-zone__label--eyebrow">{label}</span>
            <span className="crash-zone__cashout-verb">
              {cashingOut ? t('Cashing out…') : t('Cash out')}
            </span>
            <span className="crash-zone__amount">
              <span className="crash-zone__amount-symbol">{symbol}</span>
              <Money value={liveText} />
            </span>
            {/* v12 (ask 3): NO live @x.xx chip — the multiplier already
                lives on the stream; here the VALUE is the story. The row
                keeps its fixed 15px height (geometry contract) holding
                only the quiet stake note. */}
            <span className="crash-zone__tag-row">
              <span className="crash-zone__stake-note">
                {t('Bet')} {symbol}
                <Money value={ridingStakeText} />
              </span>
            </span>
          </div>
        </button>
        {footer}
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────
  // C) LOCKED / SUBMITTED stake card — the anchor. From the moment the
  //    round's submission fires until the climb starts, the staked zone
  //    keeps its chip stack + amount rendered and locked:
  //      - placing   (`--placing`)  — POSTs in flight, no bet id yet.
  //      - confirmed (confirm ring) — bet accepted; brief green pulse.
  //    Then the branches above morph it into the cash-out card. No empty
  //    void — and no geometry change — at any point.
  // ─────────────────────────────────────────────────────────────────────
  const lockedAmount = myBet ? myBet.betAmount : submittedStake
  const lockedText = formatMoney(lockedAmount, inputPrecision)
  // "Bet placed · @ x.xx" states the SUBMITTED terms (snapshot-first — the
  // raw myBet field is lossy post-replacement, the v10 mechanism); without
  // a snapshot (remount) fall back to the resolved locked terms.
  const autoTarget = submittedCashout
    ? submittedCashout.enabled
      ? submittedCashout.multiplier
      : undefined
    : lockedTerms.enabled
      ? lockedTerms.multiplier
      : undefined
  const lockedClass = [
    'crash-zone__surface',
    'crash-zone__surface--card',
    'crash-zone__surface--staked',
    'crash-zone__surface--locked',
    cardState === 'placing' ? 'crash-zone__surface--placing' : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="crash-zone">
      <div
        className={lockedClass}
        aria-label={`${label}: ${t('Bet placed')} ${symbol}${lockedText}`}
      >
        {confirmedAt > 0 && <span className="crash-zone__confirm-ring" aria-hidden />}
        <div key={contentGroup} className="crash-zone__content">
          <span className="crash-zone__label crash-zone__label--eyebrow">{label}</span>
          <ZoneChipStack amount={lockedAmount} chips={chips} />
          <span className="crash-zone__amount">
            <span className="crash-zone__amount-symbol">{symbol}</span>
            <Money value={lockedText} />
          </span>
          <span className="crash-zone__tag crash-zone__tag--locked">
            <span className="crash-zone__tag-label">{t('Bet placed')}</span>
            {autoTarget ? ` · @ ${autoTarget.toFixed(2)}x` : ''}
          </span>
        </div>
      </div>
      {footer}
    </div>
  )
}
